import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/primitives";
import { api } from "../lib/api";
import { useLang } from "../i18n";

const Insights = () => {
  const { t, lang, categories } = useLang();
  const [articles, setArticles] = useState([]);
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/articles", { params: cat !== "All" ? { category: cat } : {} })
      .then((r) => setArticles(r.data)).finally(() => setLoading(false));
  }, [cat]);

  const title = (a) => (lang === "es" ? a.title_es : a.title_en) || a.title_en;
  const excerpt = (a) => (lang === "es" ? a.excerpt_es : a.excerpt_en) || a.excerpt_en;
  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="overflow-hidden">
      <PageHero over={t.insights.heroOver} title={t.insights.heroTitle} sub={t.insights.heroSub} />

      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <div className="flex flex-wrap gap-2 mb-14">
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${cat === c ? "wanm-gradient-bg text-white" : "bg-wanm-offwhite text-wanm-dark/70 hover:text-wanm-dark"}`}>
                {c}
              </button>
            ))}
          </div>

          {loading ? <p className="text-wanm-dark/40">…</p> :
            articles.length === 0 ? <p className="text-wanm-dark/50 text-xl py-20">{t.insights.empty}</p> : (
              <>
                {featured && (
                  <FadeUp>
                    <Link to={`/insights/${featured.slug}`} data-testid="featured-article"
                      className="group grid lg:grid-cols-2 gap-8 lg:gap-14 items-center mb-20 pb-20 border-b border-wanm-dark/10">
                      <div className="relative rounded-3xl overflow-hidden aspect-[16/11]">
                        <img src={featured.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                      <div>
                        <span className="text-sm font-black uppercase tracking-widest text-wanm-red">{featured.category}</span>
                        <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tight text-wanm-dark leading-[1.05] group-hover:wanm-gradient-text transition-all">{title(featured)}</h2>
                        <p className="mt-5 text-lg text-wanm-dark/60 max-w-lg">{excerpt(featured)}</p>
                        <span className="mt-8 inline-flex items-center gap-2 font-bold text-wanm-dark group-hover:gap-3 transition-all">{t.insights.read} <ArrowUpRight size={20} /></span>
                      </div>
                    </Link>
                  </FadeUp>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {rest.map((a, i) => (
                    <FadeUp key={a.id} delay={(i % 3) * 0.08}>
                      <Link to={`/insights/${a.slug}`} data-testid={`article-card-${i}`} className="group block">
                        <div className="relative rounded-2xl overflow-hidden aspect-[4/3] mb-5">
                          <img src={a.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                        <span className="text-xs font-black uppercase tracking-widest text-wanm-red">{a.category}</span>
                        <h3 className="mt-2 text-2xl font-black text-wanm-dark leading-tight group-hover:wanm-gradient-text transition-all">{title(a)}</h3>
                        <p className="mt-3 text-wanm-dark/60">{excerpt(a)}</p>
                      </Link>
                    </FadeUp>
                  ))}
                </div>
              </>
            )}
        </div>
      </section>
    </div>
  );
};

export default Insights;
